import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import nhlRosters from '@/data/nhl_rosters.json';
import nflRosters from '@/data/nfl_rosters.json';
import { type Sport } from '@/utils/jersey-mapping';

const CDN_BASE_URL = 'https://saracenpickemdev.blob.core.windows.net/jerseys';

interface Player {
  name: string;
  jerseyNo: number;
  position?: string; // NFL: QB, RB, WR, TE, DE, etc.
}

interface TeamRoster {
  teamName: string;
  players: Player[];
}

interface PlayerSelection {
  playerName: string;
  jerseyNo: number;
  teamName: string;
  jerseyUrl: string;
}

interface PlayerSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (selection: PlayerSelection) => void;
  selectedTeams: string[];
  fieldLabel?: string;
}

export function PlayerSelectorModal({
  open,
  onOpenChange,
  onSelect,
  selectedTeams,
  fieldLabel = 'Option',
}: PlayerSelectorModalProps) {
  const [selectedSport, setSelectedSport] = useState<Sport>('NHL');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [selectedPlayer, setSelectedPlayer] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [imageError, setImageError] = useState(false);

  // Get rosters for selected sport
  const rosters = useMemo(() => {
    return (selectedSport === 'NHL' ? nhlRosters : nflRosters) as TeamRoster[];
  }, [selectedSport]);

  // Reset team and player when sport changes
  useEffect(() => {
    setSelectedTeam('');
    setSelectedPlayer('');
  }, [selectedSport]);

  const availableTeams = useMemo(() => {
    if (selectedTeams.length === 0) {
      return rosters;
    }
    return rosters.filter(roster => selectedTeams.includes(roster.teamName));
  }, [selectedTeams, rosters]);

  const currentRoster = useMemo(() => {
    return rosters.find(r => r.teamName === selectedTeam);
  }, [selectedTeam, rosters]);

  const selectedPlayerData = useMemo(() => {
    if (!currentRoster || !selectedPlayer) return null;
    return currentRoster.players.find(p => p.name === selectedPlayer);
  }, [currentRoster, selectedPlayer]);

  useEffect(() => {
    if (selectedTeam && selectedPlayerData) {
      let url: string;
      const teamKey = selectedTeam.replace(/\s+/g, '_');

      if (selectedSport === 'NFL') {
        // NFL: Use Azure CDN, same jersey for all players (no numbers)
        url = `${CDN_BASE_URL}/nfl/${teamKey}.png`;
      } else {
        // NHL: Use Azure CDN with player's jersey number
        url = `${CDN_BASE_URL}/${teamKey}_${selectedPlayerData.jerseyNo}.png`;
      }

      setPreviewUrl(url);
      setImageError(false);
    } else {
      setPreviewUrl('');
    }
  }, [selectedTeam, selectedPlayerData, selectedSport]);

  const handleSelect = () => {
    if (selectedTeam && selectedPlayerData && previewUrl) {
      onSelect({
        playerName: selectedPlayerData.name,
        jerseyNo: selectedPlayerData.jerseyNo,
        teamName: selectedTeam,
        jerseyUrl: previewUrl,
      });
      handleClose();
    }
  };

  const handleClose = () => {
    setSelectedSport('NHL');
    setSelectedTeam('');
    setSelectedPlayer('');
    setPreviewUrl('');
    setImageError(false);
    onOpenChange(false);
  };

  useEffect(() => {
    if (availableTeams.length === 1 && !selectedTeam) {
      setSelectedTeam(availableTeams[0].teamName);
    }
  }, [availableTeams, selectedTeam]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Select Player {fieldLabel && `for ${fieldLabel}`}</DialogTitle>
          <DialogDescription>
            {selectedTeams.length > 0
              ? 'Choose a sport and player from the selected teams'
              : `Select a sport and player from all ${selectedSport} teams`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="sport-select">Sport</Label>
            <Select value={selectedSport} onValueChange={(value: Sport) => setSelectedSport(value)}>
              <SelectTrigger id="sport-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NHL">NHL (Hockey)</SelectItem>
                <SelectItem value="NFL">NFL (Football)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-select">Team</Label>
            <Select
              value={selectedTeam}
              onValueChange={value => {
                setSelectedTeam(value);
                setSelectedPlayer('');
              }}
            >
              <SelectTrigger id="team-select">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                {availableTeams.map(roster => (
                  <SelectItem key={roster.teamName} value={roster.teamName}>
                    {roster.teamName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedTeam && currentRoster && (
            <div className="space-y-2">
              <Label htmlFor="player-select">Player</Label>
              <Select value={selectedPlayer} onValueChange={setSelectedPlayer}>
                <SelectTrigger id="player-select">
                  <SelectValue placeholder="Select a player" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {currentRoster.players.map(player => (
                    <SelectItem key={player.name} value={player.name}>
                      {player.name} #{player.jerseyNo}
                      {player.position && ` (${player.position})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {previewUrl && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="relative w-full h-64 border rounded-md overflow-hidden bg-gray-50">
                {!imageError ? (
                  <img
                    src={previewUrl}
                    alt="Player jersey preview"
                    className="w-full h-full object-contain"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="flex w-full h-full items-center justify-center text-sm text-muted-foreground">
                    Failed to load jersey image
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground break-all">{previewUrl}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!selectedPlayer || !previewUrl || imageError}>
            Select
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
